import { formatCents, formatCount, tt } from '@agos/shared'

/**
 * §9.6's driver wallet — *what I took today, and what of it I hand over*.
 *
 * The port of `artifacts/driver-app/app/(tabs)/wallet.tsx` (1 176 lines). One endpoint feeds all of it:
 * `GET /employees/my-wallet` answers the employee, their vehicle, six lifetime totals and **every** order
 * they have ever carried — 1 841 rows, 40 045,35 €, for the driver in this data.
 *
 * **FB-185, three things the measurement found, all in the same shape: a number that is not what its name
 * says.**
 *
 * 1. **`cardTotal` is not the card total.** `WalletService` builds it from
 *    `Set.of("card_terminal", "card_online", "card")` — `card_courier` is **not** in that set. For this
 *    driver `cardTotal` is 8 110,55 € while `cardCourierTotal` alone is 15 870,80 €. So a *cash vs card*
 *    pair built from the two headline fields understates the card side by two thirds. Legacy sidesteps
 *    this by recomputing everything client-side and never reading the field; anything else falls in. This
 *    module never prints `cardTotal`. It names the four methods, and where it needs one figure it is the
 *    cash — see below for why that is the cash and nothing else.
 * 2. **The list is not completed orders.** `COUNTED` is `status NOT IN ('cancelled', 'pending')`, which is
 *    deliberate and documented in the service (a waitress mid-shift once saw less than her manager did).
 *    But it means `new` and `preparing` orders sit in the wallet — 2 of them right now, 38,35 € — while
 *    legacy's empty state promises *"Tu sa zobrazia vaše dokončené objednávky"*. Lifetime that is noise;
 *    on a *today* pill, an order still in the oven is a real part of a small number. So the rows say
 *    which ones are not delivered yet, and the section is called what it is.
 * 3. **The list and the total disagree by design.** `orders` carries pending online payments; `walletTotal`
 *    excludes them, because *"an online card payment nobody has confirmed is not money"*. Recomputing a
 *    day total from the list — which is exactly what legacy does for every day pill — therefore counts
 *    money the server does not. One such order exists today (28,35 €, no driver), so the visible
 *    difference is zero; the divergence is structural, not rare. {@link countsAsMoney} applies the
 *    server's own rule, and {@link withheldNote} says so when it has withheld something, because a total
 *    that silently drops a row is the same bug wearing the other coat.
 *
 * **What is actually handed over is the cash, and only the cash.** `HandoverReconciliation` says it in as
 * many words: *"Everything the person did not end up holding comes off the day's takings: money paid
 * online never reached them, the card terminal's batch is already at the bank…"* A card payment taken at
 * the door is collected by the driver but never held by them — it settles to the restaurant's account. The
 * 363 recorded handovers agree: of the 25 where both card kinds are non-zero, the driver's typed card
 * deduction equals `terminal + courier` in **17** and equals just one of them in **none**.
 *
 * And that deduction is deliberately the figure **read off the terminal**, not the one the orders imply —
 * *"those two disagree when a payment was taken outside the system, and the terminal is what the bank will
 * settle."* So this screen states the cash it can see and leaves the rest to the calculator, rather than
 * computing a handover figure out of card totals it has no business trusting.
 *
 * Everything here is integer cents (§14.7) and every day boundary is the device's own midnight — a driver
 * finishing at 01:00 is asking about the shift they just worked, not about UTC.
 */

export interface WalletOrder {
  id: number
  orderNumber: string
  status: string
  totalCents: number
  paymentMethod: string
  paymentStatus: string | null
  customerName: string | null
  customerAddress: string | null
  createdAt: string | null
  completedAt: string | null
  isPreorder: boolean
  scheduledAt: string | null
}

export interface WalletOwner {
  name: string
  position: string | null
  showWallet: boolean
  isExternal: boolean
  lifetimeCents: number
  lifetimeOrders: number
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function euros(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 100)
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
  }
  return 0
}

export function ownerOf(payload: unknown): WalletOwner | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (typeof row.id !== 'number') return null
  return {
    name: text(row.name) ?? '',
    position: text(row.position),
    // The server sends `true` when the column is null, so absence is not a reason to hide anything.
    showWallet: row.showWallet !== false,
    isExternal: row.isExternal === true,
    lifetimeCents: euros(row.walletTotal),
    lifetimeOrders: typeof row.orderCount === 'number' ? row.orderCount : 0,
  }
}

export function walletOrdersOf(payload: unknown): WalletOrder[] {
  const rows = (payload as { orders?: unknown })?.orders
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        orderNumber: text(row.orderNumber) ?? String(row.id),
        status: text(row.status) ?? '',
        totalCents: euros(row.total),
        paymentMethod: text(row.paymentMethod) ?? '',
        paymentStatus: text(row.paymentStatus),
        customerName: text(row.customerName),
        customerAddress: text(row.customerAddress),
        createdAt: text(row.createdAt),
        completedAt: text(row.completedAt),
        isPreorder: row.isPreorder === true,
        scheduledAt: text(row.scheduledAt),
      },
    ]
  })
}

// ─── Which day an order belongs to ───────────────────────────────────────────

/**
 * The day a driver would file this order under.
 *
 * A preorder taken on Monday for Saturday is Saturday's work, so legacy keys it on `scheduledAt` — the one
 * piece of its date handling that is right, and kept. Everything else here is local: the boundary is the
 * device's own midnight, because a driver finishing at 01:00 means the shift they just worked.
 */
export function effectiveStamp(order: WalletOrder): string | null {
  return order.isPreorder && order.scheduledAt !== null ? order.scheduledAt : order.createdAt
}

export function dayKeyOf(order: WalletOrder): string | null {
  const stamp = effectiveStamp(order)
  if (stamp === null) return null
  const parsed = new Date(stamp)
  if (Number.isNaN(parsed.getTime())) return null
  return localDay(parsed)
}

function localDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * `Dnes` / `Včera` / `dd.mm.` — with `today` injected, per FB-171.
 *
 * A default of "now" read at module load is what made the dashboard's ranges wrong when a tab stayed open
 * across midnight, and it makes a test that passes at 23:59 fail at 00:01.
 */
export function dayLabel(key: string, today: Date): string {
  if (key === localDay(today)) return tt('Dnes')
  const yesterday = new Date(today.getTime())
  yesterday.setDate(yesterday.getDate() - 1)
  if (key === localDay(yesterday)) return tt('Včera')
  const [, month, day] = key.split('-')
  return `${day ?? ''}.${month ?? ''}.`
}

// ─── What counts as money ────────────────────────────────────────────────────

/**
 * The server's own rule, applied to a client-side sum.
 *
 * `WalletService`: *"An online card payment nobody has confirmed is not money. It stays in the list — the
 * order happened — but it is not counted, because the alternative is a handover short by its value."* The
 * row stays visible here for the same reason; it just does not join the total.
 */
export function countsAsMoney(order: WalletOrder): boolean {
  if (order.paymentMethod !== 'card_online' && order.paymentMethod !== 'card') return true
  return order.paymentStatus === 'online_paid'
}


/** An order that is in the wallet but has not been delivered yet — `new`, `preparing`, `on_the_way`. */
export function isOutstanding(order: WalletOrder): boolean {
  return order.status !== 'delivered'
}

export interface DayTotals {
  count: number
  totalCents: number
  cashCents: number
  courierCents: number
  terminalCents: number
  onlineCents: number
  withheldCents: number
  withheldCount: number
  outstandingCount: number
}

// A plain integer reduce: `sumCents` parses its inputs and would multiply cents by a hundred (FB-056).
function sum(orders: readonly WalletOrder[]): number {
  return orders.reduce((total, order) => total + order.totalCents, 0)
}

function where(orders: readonly WalletOrder[], method: string): number {
  return sum(orders.filter((order) => order.paymentMethod === method))
}

export function dayTotals(orders: readonly WalletOrder[]): DayTotals {
  const counted = orders.filter(countsAsMoney)
  const withheld = orders.filter((order) => !countsAsMoney(order))
  return {
    count: orders.length,
    totalCents: sum(counted),
    cashCents: where(counted, 'cash'),
    courierCents: where(counted, 'card_courier'),
    terminalCents: where(counted, 'card_terminal'),
    onlineCents: sum(
      counted.filter((order) => order.paymentMethod === 'card_online' || order.paymentMethod === 'card'),
    ),
    withheldCents: sum(withheld),
    withheldCount: withheld.length,
    outstandingCount: orders.filter(isOutstanding).length,
  }
}

export interface WalletDay {
  key: string
  totalCents: number
  count: number
}

/** The days that have orders, newest first, each with the figure the pill shows. */
export function daysOf(orders: readonly WalletOrder[]): WalletDay[] {
  const byDay = new Map<string, WalletOrder[]>()
  for (const order of orders) {
    const key = dayKeyOf(order)
    if (key === null) continue
    const bucket = byDay.get(key)
    if (bucket === undefined) byDay.set(key, [order])
    else bucket.push(order)
  }
  return [...byDay.entries()]
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([key, rows]) => ({ key, totalCents: dayTotals(rows).totalCents, count: rows.length }))
}

export function ordersOnDay(orders: readonly WalletOrder[], day: string | null): WalletOrder[] {
  if (day === null) return [...orders]
  return orders.filter((order) => dayKeyOf(order) === day)
}

// ─── What the screen says ────────────────────────────────────────────────────

/**
 * What the driver is holding, and what still has to come off it.
 *
 * Not the day total: an online payment never reached them and a card taken at the door settled to the
 * restaurant. What is in the bag is the cash — and the amount actually handed over is that minus fuel,
 * purchases and payouts, which is the calculator's job and not this screen's.
 */
export function cashNote(totals: DayTotals): string {
  return `${tt('V hotovosti máte')} ${formatCents(totals.cashCents)}. ${tt('Karta a online platby idú priamo reštaurácii - neodovzdávate ich. Tankovanie, nákupy a výplaty si odrátate v kalkulačke.')}`
}

/** Said only when a row was left out, because a total that silently drops one is worse than no total. */
export function withheldNote(totals: DayTotals): string {
  if (totals.withheldCount === 0) return ''
  return `${formatCount(totals.withheldCount)} ${tt('objednávok sa nezapočítava')} (${formatCents(totals.withheldCents)}) - ${tt('online platbu nikto nepotvrdil, takže to zatiaľ nie sú peniaze. V zozname zostávajú.')}`
}

/** Said only when something is still open, because on a single day that is a real part of the figure. */
export function outstandingNote(totals: DayTotals): string {
  if (totals.outstandingCount === 0) return ''
  return `${formatCount(totals.outstandingCount)} ${tt('z týchto objednávok ešte nie je doručených - sú v sume, lebo sú vaše, ale ešte ich nemáte za sebou.')}`
}

export function lifetimeNote(owner: WalletOwner): string {
  return `${tt('Celkovo za celý čas')}: ${formatCents(owner.lifetimeCents)} ${tt('z')} ${formatCount(owner.lifetimeOrders)} ${tt('objednávok. To nie je dnešná tržba.')}`
}

/**
 * `show_wallet` is off.
 *
 * The server still sends every order — it hides nothing — so the hiding is the client's job, and saying
 * *why* is what stops a driver assuming the app is broken. Measured: **0 of 23** employees have it off, so
 * this state is unreachable in this data and is built from the flag, not from a fixture.
 */
export function hiddenNote(): string {
  return tt(
    'Správca vypol zobrazenie tržby a histórie objednávok pre váš účet. Ak to je omylom, povie vám to on - nie je to chyba aplikácie.',
  )
}

export function methodLabel(paymentMethod: string): string {
  if (paymentMethod === 'cash') return tt('Hotovosť')
  if (paymentMethod === 'card_courier') return tt('Karta u kuriéra')
  if (paymentMethod === 'card_terminal') return tt('Terminál')
  if (paymentMethod === 'card_online' || paymentMethod === 'card') return tt('Online')
  return paymentMethod === '' ? tt('neuvedené') : paymentMethod
}

export function statusNote(order: WalletOrder): string {
  if (!isOutstanding(order)) return ''
  if (order.status === 'on_the_way') return tt('na ceste')
  if (order.status === 'at_address') return tt('na adrese')
  if (order.status === 'preparing') return tt('ešte sa pripravuje')
  if (order.status === 'new') return tt('ešte neprevzatá')
  return order.status
}
